import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, EyeOff, Loader2 } from "lucide-react";
import { useMyProfileHidden } from "@/hooks/useMyProfileHidden";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface SendFriendRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipientName: string;
  onSend: (note?: string) => void | Promise<unknown>;
  loading?: boolean;
  initialNote?: string;
}

const SUGGESTION_PROMPTS = [
  "Hi! Would love to say hello 🌸",
  "We met at Bertie & Boo! ☕",
  "Fancy a playdate soon? 🎉",
];

export const SendFriendRequestDialog = ({
  open,
  onOpenChange,
  recipientName,
  onSend,
  loading,
  initialNote,
}: SendFriendRequestDialogProps) => {
  const [note, setNote] = useState(initialNote ?? "");
  const { hidden: hookHidden } = useMyProfileHidden();
  const { user } = useAuth();
  const [freshHidden, setFreshHidden] = useState<boolean | null>(null);
  const [checkingHidden, setCheckingHidden] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNote(initialNote ?? "");
    if (!user) {
      setFreshHidden(false);
      return;
    }
    let cancelled = false;
    setCheckingHidden(true);
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("profile_hidden")
        .eq("id", user.id)
        .maybeSingle();
      if (!cancelled) {
        setFreshHidden(!!data?.profile_hidden);
        setCheckingHidden(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, initialNote, user?.id]);

  const myProfileHidden = freshHidden ?? hookHidden;


  const handleSend = async () => {
    if (sending || loading) return;
    setSending(true);
    try {
      const result = await onSend(note.trim() || undefined);
      if (typeof result === "object" && result !== null && "success" in result && result.success === false) return;
      setNote("");
      onOpenChange(false);
    } finally {
      setSending(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setNote(suggestion);
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !(sending || loading) && onOpenChange(nextOpen)}>
      <DialogContent className="sm:max-w-md">
        {checkingHidden && freshHidden === null ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : myProfileHidden ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <EyeOff className="h-5 w-5 text-muted-foreground" />
                Your profile is hidden
              </DialogTitle>
              <DialogDescription>
                To say hi to {recipientName}, others need to be able to see who's reaching out.
                Unhide your profile in your settings and try again — you can hide it again anytime.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={sending || loading}>
                Not now
              </Button>
              <Button asChild onClick={() => onOpenChange(false)}>
                <Link to={user ? `/user/${user.id}` : "/auth"}>Go to my profile</Link>
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span className="text-xl">🌸</span>
                Say hi to {recipientName}
              </DialogTitle>
              <DialogDescription>
                Break the ice — add a short note if you like. If they say hi back, you'll be connected as friends.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <Textarea
                placeholder="Hey! Would love to say hi…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={200}
                rows={3}
                className="resize-none"
              />
              <div className="flex flex-wrap gap-2">
                {SUGGESTION_PROMPTS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleSuggestionClick(s)}
                    className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-right">
                {note.length}/200
              </p>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSend} loading={sending || loading} loadingLabel="Sending…">
                <Send className="h-4 w-4 mr-2" />
                Send hi
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
